import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { spaceConquestApi, type FleetOrderType, type FleetType, type SpaceStructureType } from '../api/spaceConquest';
import { useToastStore } from '../stores/toastStore';

export function useSpaceConquestMap(enabled = true) {
  return useQuery({
    queryKey: ['space-conquest', 'map'],
    queryFn: () => spaceConquestApi.getMap().then((res) => res.data.data),
    enabled,
    staleTime: 10000,
    refetchInterval: 30000,
  });
}

function invalidate(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: ['space-conquest', 'map'] });
  queryClient.invalidateQueries({ queryKey: ['planet', 'me'] });
  queryClient.invalidateQueries({ queryKey: ['credits', 'transactions'] });
}

function errorMessage(error: any, fallback: string) {
  return error.response?.data?.error?.message || fallback;
}

export function useCreateSpacePort() {
  const queryClient = useQueryClient();
  const addToast = useToastStore((s) => s.addToast);
  return useMutation({
    mutationFn: (input: { gridX: number; gridY: number; name?: string; structureType?: SpaceStructureType }) => spaceConquestApi.createPort(input).then((res) => res.data.data),
    onSuccess: (_data, variables) => {
      invalidate(queryClient);
      addToast(variables.structureType === 'harvester' ? 'Harvester construction complete.' : 'Spaceport construction complete.', 'success');
    },
    onError: (error: any) => addToast(errorMessage(error, 'Could not build structure'), 'error'),
  });
}

export function useUpgradeSpacePort() {
  const queryClient = useQueryClient();
  const addToast = useToastStore((s) => s.addToast);
  return useMutation({
    mutationFn: (portId: string) => spaceConquestApi.upgradePort(portId).then((res) => res.data.data),
    onSuccess: () => {
      invalidate(queryClient);
      addToast('Spaceport upgraded.', 'success');
    },
    onError: (error: any) => addToast(errorMessage(error, 'Could not upgrade spaceport'), 'error'),
  });
}

export function useCreateFleet() {
  const queryClient = useQueryClient();
  const addToast = useToastStore((s) => s.addToast);
  return useMutation({
    mutationFn: (input: { fleetType: FleetType; originPortId?: string; name?: string }) => spaceConquestApi.createFleet(input).then((res) => res.data.data),
    onSuccess: () => {
      invalidate(queryClient);
      addToast('Fleet launched.', 'success');
    },
    onError: (error: any) => addToast(errorMessage(error, 'Could not build fleet'), 'error'),
  });
}

export function useOrderFleet() {
  const queryClient = useQueryClient();
  const addToast = useToastStore((s) => s.addToast);
  return useMutation({
    mutationFn: ({ fleetId, input }: { fleetId: string; input: { orderType?: FleetOrderType; gridX?: number; gridY?: number; targetPortId?: string; cargoCredits?: number; cargoEnergy?: number } }) =>
      spaceConquestApi.orderFleet(fleetId, input).then((res) => res.data.data),
    onSuccess: () => {
      invalidate(queryClient);
      addToast('Fleet orders confirmed.', 'success');
    },
    onError: (error: any) => addToast(errorMessage(error, 'Could not order fleet'), 'error'),
  });
}

export function useResearchSpaceTechnology() {
  const queryClient = useQueryClient();
  const addToast = useToastStore((s) => s.addToast);
  return useMutation({
    mutationFn: (definitionId: string) => spaceConquestApi.researchTechnology(definitionId).then((res) => res.data.data),
    onSuccess: () => {
      invalidate(queryClient);
      addToast('Research started.', 'success');
    },
    onError: (error: any) => addToast(errorMessage(error, 'Could not start research'), 'error'),
  });
}

export function usePostSpaceTradeOffer() {
  const queryClient = useQueryClient();
  const addToast = useToastStore((s) => s.addToast);
  return useMutation({
    mutationFn: (input: { offeredResource?: 'credits' | 'energy'; offeredAmount?: number; requestedResource?: 'credits' | 'energy'; requestedAmount?: number; offerId?: string }) =>
      spaceConquestApi.postTradeOffer(input).then((res) => res.data.data),
    onSuccess: () => {
      invalidate(queryClient);
      addToast('Trade updated.', 'success');
    },
    onError: (error: any) => addToast(errorMessage(error, 'Could not update trade'), 'error'),
  });
}
